import { App, Badge, Button, ConfigProvider, Dropdown, Flex, Layout, Typography } from 'antd';
import type { ThemeConfig } from 'antd';
import {
  EllipsisOutlined,
  FileTextOutlined,
  LogoutOutlined,
  ShopOutlined,
  ShoppingOutlined,
  ShoppingCartOutlined,
  UnorderedListOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useStoreContext } from '@/shared/lib/store-context';
import { useBreakpoint } from '@/shared/lib/hooks';
import { clientPath, ROUTES } from '@/shared/config/routes';
import { LogoutButton } from '@/features/auth/logout';
import { useLogoutMutation } from '@/entities/user';
import { useCartItemCount } from '@/entities/cart';
import { clientTheme } from '@/app/providers/theme';
import './ClientLayout.mobile.css';

const { Content, Footer } = Layout;

const TABS = [
  { suffix: '/documents', label: 'Документы', icon: <FileTextOutlined /> },
  { suffix: '/payments', label: 'Оплаты', icon: <WalletOutlined /> },
  { suffix: '/catalog', label: 'Каталог', icon: <ShoppingOutlined /> },
  { suffix: '/cart', label: 'Корзина', icon: <ShoppingCartOutlined /> },
  { suffix: '/order-requests', label: 'Заявки', icon: <UnorderedListOutlined /> },
];

/** Телефон: цели касания 44px (Apple HIG) — поверх `clientTheme`, который остаётся общим и не меняется. */
const clientMobileTheme: ThemeConfig = {
  ...clientTheme,
  token: { ...clientTheme.token, controlHeight: 44, controlHeightSM: 36, controlHeightLG: 48 },
};

/** §9: кабинет клиента — отдельная тема (14px/36px/без скруглений), нижний таб-бар под телефон. */
export function ClientLayout() {
  const ctx = useStoreContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useBreakpoint();

  return (
    <ConfigProvider theme={isMobile ? clientMobileTheme : clientTheme} componentSize={isMobile ? 'middle' : undefined}>
      <Layout className={isMobile ? 'client-shell' : undefined} style={isMobile ? undefined : { minHeight: '100vh' }}>
        <Content className={isMobile ? 'client-content' : undefined} style={isMobile ? undefined : { padding: 12, paddingBottom: 72 }}>
          {isMobile ? (
            <ClientMobileHeader storeName={ctx.storeName} />
          ) : (
            /* Выход обязателен: на общем устройстве следующий клиент должен войти под своей учётной
               записью, а не продолжить в чужом кабинете. */
            <Flex justify="space-between" align="center" gap={8}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {ctx.storeName}
              </Typography.Text>
              <Flex align="center">
                <Button type="text" onClick={() => void navigate(ROUTES.stores)}>
                  Мои магазины
                </Button>
                <LogoutButton />
              </Flex>
            </Flex>
          )}
          <Outlet />
        </Content>
        {isMobile ? (
          <ClientTabBar storeId={ctx.storeId} pathname={location.pathname} onNavigate={(path) => void navigate(path)} />
        ) : (
          <Footer
            style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              padding: 0,
              background: '#fff',
              borderTop: '1px solid #d9d9d9',
              zIndex: 10,
            }}
          >
            <Flex>
              {TABS.map((tab) => {
                const path = clientPath(ctx.storeId, tab.suffix);
                const active = location.pathname.startsWith(path);
                return (
                  <Flex
                    key={tab.suffix}
                    vertical
                    align="center"
                    justify="center"
                    gap={2}
                    style={{ flex: 1, padding: '8px 0', color: active ? '#1677ff' : undefined, cursor: 'pointer' }}
                    onClick={() => void navigate(path)}
                  >
                    {tab.icon}
                    <Typography.Text style={{ fontSize: 11, color: active ? '#1677ff' : undefined }}>{tab.label}</Typography.Text>
                  </Flex>
                );
              })}
            </Flex>
          </Footer>
        )}
      </Layout>
    </ConfigProvider>
  );
}

/** Шапка телефона: название магазина и меню аккаунта «⋯». «Мои магазины» и выход остаются
 *  доступными (на общем устройстве следующий клиент должен войти под своей учётной записью). */
function ClientMobileHeader({ storeName }: { storeName: string }) {
  const { modal } = App.useApp();
  const navigate = useNavigate();
  const [logout] = useLogoutMutation();

  function confirmLogout() {
    modal.confirm({
      title: 'Выйти из аккаунта?',
      onOk: () => {
        void logout();
      },
    });
  }

  return (
    <header className="client-header">
      <span className="client-header__title">{storeName}</span>
      <Dropdown
        trigger={['click']}
        placement="bottomRight"
        overlayClassName="client-account-menu"
        menu={{
          items: [
            { key: 'stores', icon: <ShopOutlined />, label: 'Мои магазины', onClick: () => void navigate(ROUTES.stores) },
            { key: 'logout', icon: <LogoutOutlined />, label: 'Выйти', danger: true, onClick: confirmLogout },
          ],
        }}
      >
        <Button type="text" icon={<EllipsisOutlined />} aria-label="Меню аккаунта" style={{ width: 44, height: 44 }} />
      </Dropdown>
    </header>
  );
}

/** Нижний таб-бар в стиле iOS: иконка + подпись 11px, цель ≥ 44px, активная вкладка выделена,
 *  на «Корзине» — число позиций. Высота 56px плюс «домашняя» полоса iPhone (см. CSS). */
function ClientTabBar({ storeId, pathname, onNavigate }: { storeId: string; pathname: string; onNavigate: (path: string) => void }) {
  const cartCount = useCartItemCount(storeId);
  const home = clientPath(storeId);

  return (
    <nav className="client-tabbar" aria-label="Разделы кабинета">
      {TABS.map((tab) => {
        const path = clientPath(storeId, tab.suffix);
        // Корень кабинета открывает «Документы», хотя адрес у него без суффикса.
        const active = pathname.startsWith(path) || (tab.suffix === '/documents' && (pathname === home || pathname === `${home}/`));
        const isCart = tab.suffix === '/cart';
        return (
          <button
            key={tab.suffix}
            type="button"
            className={active ? 'client-tab client-tab--active' : 'client-tab'}
            aria-current={active ? 'page' : undefined}
            aria-label={isCart && cartCount > 0 ? `${tab.label}, позиций: ${cartCount}` : tab.label}
            onClick={() => onNavigate(path)}
          >
            <span className="client-tab__icon" aria-hidden>
              {isCart ? (
                <Badge count={cartCount} size="small" overflowCount={99} offset={[6, -2]}>
                  {tab.icon}
                </Badge>
              ) : (
                tab.icon
              )}
            </span>
            <span className="client-tab__label">{tab.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
